import type { FetchError } from 'ofetch'
import { useToast } from 'vue-toastification'
import { useMyFetch } from '~/features/shared/useMyFetch'
import type { CalculateOrderResponse, GetOrdersRouteSheetPayload, Order, OrderDTO, OrderRouteItem, SetDriverBulkPayload } from '~/model/interfaces/Order/types'
import { getFormDataFromObject } from '~/helpers/objectToFormData'
import { getValidErrorMessage } from '~/helpers/getValidErrorMessage'

export const useOrder = () => {
    const toast = useToast()
    const { $api } = useNuxtApp()
    const order = ref<Order>()

    const calculatedData = ref<CalculateOrderResponse | null>(null)
    const isCalculated = ref(false)

    function setIsCalculatedValue(value: boolean) {
        isCalculated.value = value
    }

    async function loadOrder(orderId: number, {
        withProducts = false,
        withComments = false,
        withHistory = false,
        withReclamations = false,
        withSupplies = false,
    } = {}) {
        try {
            order.value = await $api(`order/get/${unref(orderId)}`, {
                params: {
                    withProducts: unref(withProducts),
                    withComments: unref(withComments),
                    withHistory: unref(withHistory),
                    withReclamations: unref(withReclamations),
                    withSupplies: unref(withSupplies),
                },
            })
        } catch (error) {
            toast.error(getValidErrorMessage(error as FetchError, 'Ошибка при загрузке заказа'))
        }
    }

    async function orderRemoveLoadedPhotos(photoIds: number[]) {
        const {
            error,
            status,
        } = await useMyFetch<{ count: number }>('order/deleteDriverLoadPhoto', {
            method: 'POST',
            body: {
                photoIds: unref(photoIds),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Фото успешно удалены')
        } else {
            toast.error(getValidErrorMessage(error))
        }
    }

    async function orderGetOrderExpectedWarehousePositions(orderId: number) {
        const {
            data,
            status,
            error,
        } = await useMyFetch<Record<string, number>>(`order/${unref(orderId)}/getExpectedWarehousePositions`)

        if (unref(status) === 'success') {
            return unref(data)
        } else {
            toast.error(getValidErrorMessage(error))
        }
    }

    async function orderUpdate(body: MaybeRef<Partial<OrderDTO>>) {
        try {
            await $api('order/updateV2', {
                method: 'POST',
                body: unref(body),
            })
            toast.success('Заказ успешно обновлен!')
        } catch (error) {
            toast.error(getValidErrorMessage(error as FetchError, 'Ошибка при попытке обновить заказ'))
        }
    }

    async function orderPatch(body: MaybeRef<Partial<OrderDTO>>) {
        const {
            status,
            error,
        } = await useMyFetch('v1/orders/patch', {
            method: 'POST',
            watch: false,
            body: unref(body),
        })

        if (unref(status) === 'success') {
            toast.success('Заказ успешно обновлен!')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка при попытке обновить заказ'))
        }
    }

    async function orderCorrectDeliveryDate(body: MaybeRef<Partial<OrderDTO>>) {
        const {
            status,
            error,
        } = await useMyFetch('order/correctDeliveryDateV2', {
            method: 'POST',
            watch: false,
            body: unref(body),
        })

        if (unref(status) === 'success') {
            toast.success('Заказ успешно обновлен!')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка при попытке обновить заказ'))
        }
    }

    async function orderAdditionalOrder(body: MaybeRef<Partial<OrderDTO>>) {
        const {
            status,
            error,
        } = await useMyFetch('order/additionalOrderV2', {
            method: 'POST',
            watch: false,
            body: unref(body),
        })

        if (unref(status) === 'success') {
            toast.success('Заказ успешно обновлен!')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка при попытке обновить заказ'))
        }
    }

    async function orderCreate(body: MaybeRef<Partial<OrderDTO>>) {
        const {
            status,
            error,
        } = await useMyFetch<CalculateOrderResponse>('order/createV2', {
            method: 'POST',
            body: unref(body),
            watch: false,
        })

        if (unref(status) === 'success') {
            toast.success('Заказ успешно создан!')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка при попытке создания заказа'))
        }
    }

    async function orderCalculate(body: MaybeRef<Partial<OrderDTO>>) {
        const {
            data,
            status,
            error,
        } = await useMyFetch<CalculateOrderResponse>('order/calculateV2', {
            method: 'POST',
            body: unref(body),
            watch: false,
        })

        if (unref(status) === 'success') {
            setIsCalculatedValue(true)
            calculatedData.value = unref(data)
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка при попытке расчета заказа'))
        }
    }

    async function orderApproveDiscount(orderId: number) {
        const {
            status,
            error,
        } = await useMyFetch('order/approveDiscount', {
            method: 'POST',
            body: {
                orderId: unref(orderId),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Заказ одобрен!')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка при попытке подтверждения'))
        }
    }

    async function orderLeadOfLogisticianApproveDriverBonus(orderId: number, driverBonus: MaybeRef<number>) {
        const {
            status,
            error,
        } = await useMyFetch('order/leadOfLogisticianApproveDriverBonus', {
            method: 'POST',
            body: {
                orderId: unref(orderId),
                driverBonus: unref(driverBonus),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Надбавка одобрена!')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка'))
        }
    }

    async function orderRejectDiscount(orderId: number) {
        const {
            status,
            error,
        } = await useMyFetch('order/rejectDiscount', {
            method: 'POST',
            body: {
                orderId: unref(orderId),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Успех!')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка при попытке отказа'))
        }
    }

    async function orderSetDriver(
        orderId: MaybeRef<number>,
        driverId: MaybeRef<number>,
        deliveryDate: MaybeRef<Date>,
    ) {
        const {
            data,
            status,
            error,
        } = await useMyFetch<{message: string}>('order/setDriverV2', {
            method: 'POST',
            body: {
                orderId: unref(orderId),
                driverId: unref(driverId),
                deliveryDate: unref(deliveryDate).getTime(),
            },
        })

        const message = unref(data)?.message
        if (unref(status) === 'success') {
            toast.success(message || 'Водитель успешно назначен')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка при попытке назначить водителя'))
        }
    }

    async function orderSetDriverBulk(payload: SetDriverBulkPayload) {
        const {
            status,
            error,
        } = await useMyFetch('order/setDriverBulk', {
            method: 'POST',
            body: payload,
        })

        if (unref(status) === 'success') {
            toast.success('Водитель успешно назначен')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка при попытке назначить водителя'))
        }
    }

    // был orderDriverReject
    async function orderDriverRejectBeforeLoad(orderId: number, rejectionReason: string) {
        const {
            status,
            error,
        } = await useMyFetch('order/driverRejectBeforeLoad', {
            method: 'POST',
            body: {
                orderId: unref(orderId),
                rejectionReason: unref(rejectionReason),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Заказ отклонен')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка при попытке отклонить заказ'))
        }
    }

    async function orderDriverRejectAfterLoad(orderId: number, rejectionReason: string) {
        const {
            status,
            error,
        } = await useMyFetch('order/driverRejectAfterLoad', {
            method: 'POST',
            body: {
                orderId: unref(orderId),
                rejectionReason: unref(rejectionReason),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Заказ отклонен')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка при попытке отклонить заказ'))
        }
    }

    async function orderNeedAdditionalOrder(orderId: number, comment: string) {
        const {
            status,
            error,
        } = await useMyFetch('order/needAdditionalOrder', {
            method: 'POST',
            body: {
                orderId: unref(orderId),
                comment: unref(comment),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Отправлено на дозаказ')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка при попытке отправить на дозаказ'))
        }
    }

    async function orderDriverNotAnswered(orderId: number) {
        try {
            await $api('order/driverNotAnswered', {
                method: 'POST',
                body: { orderId: unref(orderId) },
            })
            toast.success('Сообщение доставлено')
        } catch (error) {
            toast.error(getValidErrorMessage(error as FetchError, 'Ошибка'))
        }
    }

    async function orderSendToDriverSelection(orderIds: number[]) {
        const {
            status,
            error,
        } = await useMyFetch('order/sendToDriverSelection', {
            method: 'POST',
            body: {
                orderIds: unref(orderIds),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Отправлено на распределение водителя')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка'))
        }
    }

    async function orderCancelByManager(orderId: number) {
        const {
            status,
            error,
        } = await useMyFetch('order/cancelByManager', {
            method: 'POST',
            body: {
                orderId: unref(orderId),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Заказ отменен')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка'))
        }
    }

    async function orderSendToLogisticianCall(orderIds: number[], logisticianId?: number) {
        const {
            status,
            error,
        } = await useMyFetch('order/sendToLogisticianCall', {
            method: 'POST',
            body: {
                orderIds: unref(orderIds),
                logisticianId: unref(logisticianId),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Отправлено на прозвон')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка'))
        }
    }

    async function orderLogisticianNotAnswered(orderId: number) {
        const {
            status,
            error,
        } = await useMyFetch('order/logisticianNotAnswered', {
            method: 'POST',
            body: {
                orderId: unref(orderId),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Сообщение доставлено')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка'))
        }
    }

    async function orderLeadOfLogisticianNotAnswered(orderId: number) {
        const {
            status,
            error,
        } = await useMyFetch('order/leadOfLogisticianNotAnswered', {
            method: 'POST',
            body: {
                orderId: unref(orderId),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Сообщение доставлено')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка'))
        }
    }

    async function orderLeadOfSalesNotAnswered(orderId: number) {
        const {
            status,
            error,
        } = await useMyFetch('order/leadOfSalesNotAnswered', {
            method: 'POST',
            body: {
                orderId: unref(orderId),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Сообщение доставлено')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка'))
        }
    }

    async function orderConfirm(orderId: number) {
        const {
            status,
            error,
        } = await useMyFetch('order/confirm', {
            method: 'POST',
            body: {
                orderId: unref(orderId),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Заказ подтвержден')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка при попытке отклонить заказ'))
        }
    }

    async function orderStartAssembly(orderId: number) {
        const {
            status,
            error,
        } = await useMyFetch('order/warehouseStartAssembly', {
            method: 'POST',
            body: {
                orderId: unref(orderId),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Успешно')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка при попытке начать сборку'))
        }
    }

    async function orderSetNotEnoughProducts(orderId: number) {
        const {
            status,
            error,
        } = await useMyFetch('order/warehouseNotEnoughProducts', {
            method: 'POST',
            body: {
                orderId: unref(orderId),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Успешно')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка'))
        }
    }

    async function orderFinishAssembly(orderId: number) {
        const {
            status,
            error,
        } = await useMyFetch('order/warehouseFinishAssembly', {
            method: 'POST',
            body: {
                orderId: unref(orderId),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Сборка завершена')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка'))
        }
    }

    async function orderDriverLoadPhotos(params: any) {
        try {
            await $api('order/driverLoadPhotos', {
                method: 'POST',
                body: getFormDataFromObject(unref(params)),
            })
            toast.success('Фото успешно отправлены!')
        } catch (error) {
            toast.error(getValidErrorMessage(error as FetchError, 'Ошибка при загрузке фотографий'))
        }
    }

    async function orderDriverDeliveryPhotos(params: any) {
        try {
            await $api('order/loadDeliveryPhotos', {
                method: 'POST',
                body: getFormDataFromObject(unref(params)),
            })
            toast.success('Фото успешно отправлены')
        } catch (error) {
            toast.error(getValidErrorMessage(error as FetchError, 'Ошибка при загрузке фотографий'))
        }
    }

    async function orderSendToLogisticianCheck(orderId: number) {
        const {
            status,
            error,
        } = await useMyFetch('order/sendToLogisticianCheck', {
            method: 'POST',
            body: {
                orderId: unref(orderId),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Отправлено на проверку логистом')
        } else {
            toast.error(getValidErrorMessage(error))
        }
    }

    async function orderLogisticianApproveLoad(orderId: number, positions: MaybeRef<object[]>) {
        const {
            status,
            error,
        } = await useMyFetch('order/logisticianApproveLoad', {
            method: 'POST',
            body: {
                orderId: unref(orderId),
                positions: unref(positions),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Погрузка подтверждена!')
        } else {
            toast.error(getValidErrorMessage(error))
        }

        return unref(status) === 'success'
    }

    async function orderLogisticianRejectLoad(payload: {orderId: number, comment?: string}) {
        const {
            status,
            error,
        } = await useMyFetch('order/logisticianRejectLoad', {
            method: 'POST',
            body: unref(payload),
        })

        if (unref(status) === 'success') {
            toast.success('Отправлено на пересборку!')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка'))
        }
    }

    async function orderSetHasDamage(orderId: number) {
        const {
            status,
            error,
        } = await useMyFetch('order/hasDamage', {
            method: 'POST',
            body: {
                orderId: unref(orderId),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Сообщение о повреждении заказа доставлено')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка'))
        }
    }

    async function orderFinishDelivery(orderId: number, comment?: string) {
        const {
            status,
            error,
        } = await useMyFetch('order/finishDelivery', {
            method: 'POST',
            body: {
                orderId: unref(orderId),
                ...(unref(comment) ? { comment: unref(comment) } : {}),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Успешно')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка'))
        }
    }

    async function orderSetFurnitureAssembler(furnitureAssemblyId: MaybeRef<number>, userId: MaybeRef<number>) {
        const {
            status,
            error,
        } = await useMyFetch('order/setFurnitureAssembler', {
            method: 'POST',
            body: {
                furnitureAssemblyId: unref(furnitureAssemblyId),
                furnitureAssemblerId: unref(userId),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Успешно назначен')
        } else {
            toast.error(getValidErrorMessage(error))
        }
    }

    async function orderStartFurnitureAssembly(furnitureAssemblyId: MaybeRef<number>) {
        const {
            status,
            error,
        } = await useMyFetch('order/startFurnitureAssembly', {
            method: 'POST',
            body: {
                furnitureAssemblyId: unref(furnitureAssemblyId),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Успешно')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка при попытке начать сборку'))
        }
    }

    async function orderFinishFurnitureAssembly(furnitureAssemblyId: number) {
        const {
            status,
            error,
        } = await useMyFetch('order/finishFurnitureAssembly', {
            method: 'POST',
            body: {
                furnitureAssemblyId: unref(furnitureAssemblyId),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Успешно')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка при попытке закончить сборку'))
        }
    }

    async function orderSetEmergency(comment: string) {
        const {
            status,
            error,
        } = await useMyFetch('order/emergency', {
            method: 'POST',
            body: {
                comment: unref(comment),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Сообщено о проишествии')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка при попытке отправить на дозаказ'))
        }
    }

    async function orderChangeDeliveryDate(orderId: number, comment: string) {
        const {
            status,
            error,
        } = await useMyFetch('order/changeDeliveryDate', {
            method: 'POST',
            body: {
                orderId: unref(orderId),
                comment: unref(comment),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Успешно')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка'))
        }
    }

    async function orderGetRouteSheet(payload: GetOrdersRouteSheetPayload) {
        const {
            data,
            status,
            error,
        } = await useMyFetch<{orders: OrderRouteItem[]}>('order/getRouteSheet', {
            method: 'POST',
            body: payload,
        })

        if (unref(status) === 'success') {
            return unref(data)
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка'))
        }
    }

    async function orderLogisticianSetRejectionReason(orderId: number, rejectionReason: string) {
        const {
            status,
            error,
        } = await useMyFetch('order/logisticianSetRejectionReason', {
            method: 'POST',
            body: {
                orderId: unref(orderId),
                rejectionReason: unref(rejectionReason),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Успешно')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка'))
        }
    }

    async function orderRejectWhileFurnitureAssembly(furnitureAssemblyId: number) {
        const {
            status,
            error,
        } = await useMyFetch('order/rejectWhileFurnitureAssembly', {
            method: 'POST',
            body: {
                furnitureAssemblyId: unref(furnitureAssemblyId),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Успешно')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка'))
        }
    }

    async function orderAddComment(orderId: number, text: string) {
        const {
            status,
            error,
        } = await useMyFetch('order/addComment', {
            method: 'POST',
            body: {
                orderId: unref(orderId),
                text,
            },
        })

        if (unref(status) === 'success') {
            return true
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка'))
            return false
        }
    }

    async function orderCalculateFurnitureAssembler(furnitureAssemblyId: number, sum: number) {
        const {
            status,
            error,
        } = await useMyFetch('order/calculateFurnitureAssembler', {
            method: 'POST',
            body: {
                furnitureAssemblyId: unref(furnitureAssemblyId),
                sum: unref(sum),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Успешно')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка при попытке закончить сборку'))
        }
    }

    async function orderSendToReturnToWarehouse(orderId: number) {
        const {
            status,
            error,
        } = await useMyFetch('order/sendToReturnToWarehouse', {
            method: 'POST',
            body: {
                orderId: unref(orderId),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Заказ отправлен на возврат товаров на склад')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка при отправке заказа на возврат товаров на склад'))
        }
    }

    async function orderReturnToWarehouseLoadPhotos(params: any) {
        try {
            await $api('order/returnToWarehouse', {
                method: 'POST',
                body: getFormDataFromObject(unref(params)),
            })
            toast.success('Фото успешно отправлены')
        } catch (error) {
            toast.error(getValidErrorMessage(error as FetchError, 'Ошибка при загрузке фотографий'))
        }
    }

    async function orderApproveReturnToWarehouse(orderId: number, warehousePositions: MaybeRef<object[]>) {
        const {
            status,
            error,
        } = await useMyFetch('order/approveReturnToWarehouse', {
            method: 'POST',
            body: {
                orderId: unref(orderId),
                warehousePositions: unref(warehousePositions),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Возврат на склад подтвержден!')
        } else {
            toast.error(getValidErrorMessage(error))
        }

        return unref(status) === 'success'
    }

    async function orderRejectReturnToWarehouse(orderId: number) {
        const {
            status,
            error,
        } = await useMyFetch('order/rejectReturnToWarehouse', {
            method: 'POST',
            body: {
                orderId: unref(orderId),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Отправлено на пересборку!')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка'))
        }
    }

    async function orderClientWaitCancel(orderId: number) {
        const {
            status,
            error,
        } = await useMyFetch('order/clientWaitCancel', {
            method: 'POST',
            body: {
                orderId: unref(orderId),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Заказ переведен в статус "Клиент ожидает отмены"')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка при переводе заказа в статус "Клиент ожидает отмены"'))
        }
    }

    async function deleteOrder(orderId: number) {
        const {
            status,
            error,
        } = await useMyFetch('order/delete', {
            method: 'POST',
            body: {
                orderId: unref(orderId),
            },
        })

        if (unref(status) === 'success') {
            toast.success('Заказ удален')
        } else {
            toast.error(getValidErrorMessage(error, 'Ошибка при удалении заказа'))
        }
    }

    async function orderRetainClientSuccess(orderId: number) {
        try {
            await $api('v1/orders/retain-client-success', {
                method: 'POST',
                body: { orderId: unref(orderId) },
            })
            toast.success('Статус заказа обновлен')
        } catch (error) {
            toast.error(getValidErrorMessage(error as FetchError, 'Ошибка'))
        }
    }

    async function createPaymentQrByDriver(body: { orderId: number; email: string }) {
        try {
            const response = await $api<{ payLink?: string }>('v1/operations/order/ozon-payment/create-by-driver', {
                method: 'POST',
                body: unref(body),
            })
            return response.payLink
        } catch (error) {
            toast.error(getValidErrorMessage(error as FetchError, 'Ошибка получения ссылки на оплату'))
        }
    }

    return {
        order,
        calculatedData,
        isCalculated,
        setIsCalculatedValue,
        loadOrder,
        orderCalculate,
        orderChangeDeliveryDate,
        orderFinishFurnitureAssembly,
        orderSetHasDamage,
        orderLogisticianApproveLoad,
        orderLogisticianRejectLoad,
        orderFinishDelivery,
        orderRejectWhileFurnitureAssembly,
        orderLogisticianSetRejectionReason,
        orderSetFurnitureAssembler,
        orderStartFurnitureAssembly,
        orderUpdate,
        orderPatch,
        orderCreate,
        orderDriverLoadPhotos,
        orderDriverDeliveryPhotos,
        orderApproveDiscount,
        orderRejectDiscount,
        orderSetDriver,
        orderSetDriverBulk,
        orderDriverRejectBeforeLoad,
        orderDriverRejectAfterLoad,
        orderNeedAdditionalOrder,
        orderDriverNotAnswered,
        orderConfirm,
        orderStartAssembly,
        orderSetNotEnoughProducts,
        orderFinishAssembly,
        orderSetEmergency,
        orderAddComment,
        orderLeadOfLogisticianApproveDriverBonus,
        orderLogisticianNotAnswered,
        orderLeadOfLogisticianNotAnswered,
        orderLeadOfSalesNotAnswered,
        orderCalculateFurnitureAssembler,
        orderSendToLogisticianCheck,
        orderGetOrderExpectedWarehousePositions,
        orderCorrectDeliveryDate,
        orderGetRouteSheet,
        orderAdditionalOrder,
        orderSendToDriverSelection,
        orderCancelByManager,
        orderSendToLogisticianCall,
        orderSendToReturnToWarehouse,
        orderReturnToWarehouseLoadPhotos,
        orderApproveReturnToWarehouse,
        orderRejectReturnToWarehouse,
        orderClientWaitCancel,
        orderRemoveLoadedPhotos,
        deleteOrder,
        orderRetainClientSuccess,
        createPaymentQrByDriver,
    }
}
